'use client'

import {
	HYPERLIQUID_ARB_USDC,
	HYPERLIQUID_BRIDGE2_ARBITRUM,
	HYPERLIQUID_MIN_DEPOSIT_USDC,
	HYPERLIQUID_SPONSORED_CHAIN,
} from '@/constants/hyperliquid'
import { sendSponsoredCalls } from '@/lib/wallet-core/sponsored'
import { useWallet } from '@/providers/WalletContext'
import { captureApiError } from '@/utils/sentry-helpers'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type Address, encodeFunctionData, erc20Abi, parseUnits } from 'viem'

const ARB_USDC = HYPERLIQUID_ARB_USDC as Address

export type DepositResult = {
	txHash: string
}

/**
 * Auto-deposits USDC from the user's Account Kit smart wallet on Arbitrum
 * into Hyperliquid. Mechanism: a single ERC20 transfer of USDC to the
 * Hyperliquid Bridge2 contract address. Hyperliquid validators watch the
 * bridge address off-chain and credit the matching HyperCore L1 account
 * within ~3-4 minutes.
 *
 * Initial implementation assumed Bridge2 exposed a `deposit(uint64)`
 * method — empirical test on mainnet showed that selector reverts
 * (Bridge2 isn't a typical deposit contract; it accepts plain transfers).
 *
 * Gas sponsored via NEXT_PUBLIC_ALCHEMY_POLICY_ID (Arbitrum enabled on the
 * existing policy). Cross-chain (Polygon/BSC/Solana → Arbitrum USDC) is
 * handled separately — internal Bridge or LI.FI — and lands USDC on the
 * smart wallet's EOA, where this hook then picks it up.
 *
 * Min deposit per Hyperliquid bridge2 spec: 5 USDC. Smaller amounts are
 * silently dropped by validators — the auto-deposit watcher pre-checks
 * this and the caller should validate too.
 */
export function useHyperliquidDeposit() {
	const { evmAccount } = useWallet()
	const qc = useQueryClient()

	return useMutation({
		mutationFn: async (input: { amountUsdc: number }): Promise<DepositResult> => {
			if (!evmAccount) throw new Error('Wallet not ready')
			if (input.amountUsdc < HYPERLIQUID_MIN_DEPOSIT_USDC) {
				throw new Error(`Minimum deposit is ${HYPERLIQUID_MIN_DEPOSIT_USDC} USDC`)
			}

			const amount = parseUnits(input.amountUsdc.toString(), 6)

			// Single ERC20 transfer to the Bridge2 address. Validators pick
			// the transfer off-chain (no contract method call needed).
			const transferData = encodeFunctionData({
				abi: erc20Abi,
				functionName: 'transfer',
				args: [HYPERLIQUID_BRIDGE2_ARBITRUM as Address, amount],
			})

			// Alchemy's paymaster simulates the userOp against an Arbitrum RPC
			// that occasionally lags behind tx propagation — the user sees a
			// 400 "ERC20: transfer amount exceeds balance" on the FIRST try
			// even though the funds are sitting on the EOA. A short retry
			// loop with exponential backoff lets the paymaster's node catch
			// up before we surface the error. Most observed races resolve on
			// the second or third attempt.
			//
			// Upfront 5s settle wait: the auto-deposit watcher reads balance
			// from one Alchemy node, but the paymaster simulates against a
			// different (or same but lagging) node. By the time the watcher
			// sees the Relay-delivered USDC, the paymaster's node can still
			// be on a pre-deposit state for several seconds. Fewer retries
			// happen if we just wait a beat upfront. 5s observed empirically
			// to clear ~80% of the races; the remaining 20% fall through to
			// the retry loop below.
			//
			// Intermediate attempts pass silent=true so the DevTools
			// console doesn't fill up with "400" errors that get recovered
			// on the next try — final attempt logs normally.
			await new Promise(r => setTimeout(r, 5_000))

			const backoffMs = [3000, 5000, 8000, 12000]
			let lastErr: unknown
			for (let attempt = 0; attempt < 5; attempt++) {
				const isLastAttempt = attempt === 4
				try {
					const txHash = await sendSponsoredCalls({
						chain: HYPERLIQUID_SPONSORED_CHAIN,
						account: evmAccount,
						calls: [{ target: ARB_USDC, data: transferData }],
						silent: !isLastAttempt,
					})
					return { txHash }
				} catch (err) {
					lastErr = err
					if (isLastAttempt) throw err
					const msg = err instanceof Error ? err.message : String(err)
					// Only retry on paymaster race fingerprints — balance
					// stale OR generic paymaster 400. Real config errors
					// (policy missing, signer dead) short-circuit.
					const isRaceCondition =
						/exceeds balance|HTTP request failed.*400|paymaster.*reverted/i.test(msg)
					if (!isRaceCondition) throw err
					await new Promise(r => setTimeout(r, backoffMs[attempt]))
				}
			}
			throw lastErr instanceof Error ? lastErr : new Error('deposit failed after 5 attempts')
		},
		onSuccess: () => {
			// Validator credit lands in ~3-4min. Balance hook polls /balance
			// every 10s so the bump will surface; this invalidation kicks
			// the next refetch sooner.
			qc.invalidateQueries({ queryKey: ['hl-balance'] })
			qc.invalidateQueries({ queryKey: ['arb-usdc-balance'] })
			qc.invalidateQueries({ queryKey: ['arb-usdc-banner'] })
		},
		// Only surfaces when ALL 5 retries (paymaster race) have failed.
		// Transient retries that succeed don't reach here. Capture with
		// amount context so we can correlate prod failures to their
		// payload size in Sentry.
		onError: (error, variables) => {
			captureApiError(error, {
				endpoint: 'hyperliquid_deposit_hook',
				extra: { amountUsdc: variables.amountUsdc },
			})
		},
	})
}
